import { itx } from "../../../../src/translationItx/itx";
import type { C1_FurnitureDefinition, C1_FurnitureRendererProps } from "../../types/C1_FurnitureTypes";
import { RoomType } from "../../types/C1_ModelTypes";
import { C1_FurnitureSvg } from "../C1_FurnitureSvg";
import { C1_FurnitureIcon } from "../C1_FurnitureIcon";

const DEFAULT_WIDTH = 2.0;
const DEFAULT_HEIGHT = 1.8;
const TABLE_SIZE_M = 0.4;

const DoubleBed: C1_FurnitureDefinition = {
  id: "doubleBed",
  icon: <C1_FurnitureIcon kind="double-bed" />,
  description: { en: "Double bed", de: "Doppelbett" },
  category: RoomType.Bedroom,
  defaultWidth: DEFAULT_WIDTH,
  defaultHeight: DEFAULT_HEIGHT,
  originXPercentage: 50,
  originYPercentage: 0,
  autoRotate: true,

  properties: [
    {
      key: "tableLeft",
      get label() { return itx("c1.ui.Tisch_links", "Tisch links"); },
      type: "boolean",
      defaultValue: true,
    },
    {
      key: "tableRight",
      get label() { return itx("c1.ui.Tisch_rechts", "Tisch rechts"); },
      type: "boolean",
      defaultValue: true,
    },
  ],

  Renderer: ({
    width,
    height,
    rotation,
    PX,
    strokeWidth,
    strokeColor,
    fillColor,
    showDetails,
    tableLeft = true,
    tableRight = true,
  }: C1_FurnitureRendererProps) => {
    const finalRotation = rotation ?? 0;
    const finalStrokeWidth = strokeWidth ?? 2;
    const finalStrokeColor = strokeColor ?? "#000000";
    const finalFillColor = fillColor ?? "#ffffff";
    const tableSize = TABLE_SIZE_M * PX;
    const inset = 0.02 * PX;
    const innerInset = 0.1 * PX;

    return (
      <C1_FurnitureSvg
        PX={PX}
        width={width + tableSize * 2}
        height={height}
        rotation={finalRotation}
        viewBoxX={-tableSize}
        viewBoxWidth={width + tableSize * 2}
        viewBoxHeight={height}
        originXPercentage={50}
        originYPercentage={0}
      >
        {tableLeft && (
          <rect
            x={-tableSize - 0.05 * PX}
            y={0.05 * PX}
            width={tableSize}
            height={tableSize}
            fill={finalFillColor}
            stroke={finalStrokeColor}
            strokeWidth={finalStrokeWidth}
          />
        )}
        {tableRight && (
          <rect
            x={width + 0.05 * PX}
            y={0.05 * PX}
            width={tableSize}
            height={tableSize}
            fill={finalFillColor}
            stroke={finalStrokeColor}
            strokeWidth={finalStrokeWidth}
          />
        )}
        <rect
          x={inset}
          y={inset}
          width={Math.max(0, width - inset * 2)}
          height={Math.max(0, height - inset * 2)}
          rx={0.03 * PX}
          fill={finalFillColor}
          stroke={finalStrokeColor}
          strokeWidth={finalStrokeWidth * 2}
        />
        <rect
          x={innerInset}
          y={innerInset}
          width={Math.max(0, width - innerInset * 2)}
          height={Math.max(0, height - innerInset * 2)}
          rx={0.04 * PX}
          fill={finalFillColor}
          stroke={finalStrokeColor}
          strokeWidth={finalStrokeWidth}
        />
        {showDetails && (
          <>
            <rect
              x={width * 0.09}
              y={0.18 * PX}
              width={width * 0.38}
              height={Math.min(0.3 * PX, height * 0.24)}
              rx={0.08 * PX}
              fill={finalFillColor}
              stroke={finalStrokeColor}
              strokeWidth={finalStrokeWidth}
            />
            <rect
              x={width * 0.53}
              y={0.18 * PX}
              width={width * 0.38}
              height={Math.min(0.3 * PX, height * 0.24)}
              rx={0.08 * PX}
              fill={finalFillColor}
              stroke={finalStrokeColor}
              strokeWidth={finalStrokeWidth}
            />
            <line x1={innerInset} y1={Math.min(0.55 * PX, height * 0.35)} x2={width - innerInset} y2={Math.min(0.55 * PX, height * 0.35)} stroke={finalStrokeColor} strokeWidth={finalStrokeWidth} />
            <line
              x1={width / 2}
              y1={Math.min(0.55 * PX, height * 0.35)}
              x2={width / 2}
              y2={height - innerInset}
              stroke={finalStrokeColor}
              strokeWidth={finalStrokeWidth * 0.75}
              strokeDasharray="6 4"
            />
          </>
        )}
      </C1_FurnitureSvg>
    );
  },
};

export default DoubleBed;
